import { DEFAULT_TIMEZONE } from './constants';
import { convertToLocalTimeZone } from './format';
import { convertTo24Hour } from './slots';

export type ZonedWallClock = {
  date: Date | null;
  time: string | null;
};

// gloss
export const zonedWallClockToUtc = (isoDate: string, hourMinute: string, timezone: string): Date => {
  const dateParts = isoDate.split('-').map(Number);
  const timeParts = hourMinute.split(':').map(Number);
  const y = dateParts[0] ?? 1970;
  const mo = dateParts[1] ?? 1;
  const d = dateParts[2] ?? 1;
  const hh = timeParts[0] ?? 0;
  const mm = timeParts[1] ?? 0;
  const utcGuess = Date.UTC(y, mo - 1, d, hh, mm, 0);

  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const offsetAt = (instant: number): number => {
    const parts = fmt.formatToParts(new Date(instant));
    const get = (t: Intl.DateTimeFormatPartTypes) => parseInt(parts.find((p) => p.type === t)?.value ?? '0', 10);
    let shownHour = get('hour');
    let shownDay = get('day');
    if (shownHour === 24) {
      shownHour = 0;
      shownDay += 1;
    }
    const shown = Date.UTC(get('year'), get('month') - 1, shownDay, shownHour, get('minute'), get('second'));

    return shown - instant;
  };

  // why: the offset read at the naive guess is the wrong one when the guess and the answer sit on
  // why: opposite sides of a DST switch, so it is read again at the corrected instant.
  const firstPass = utcGuess - offsetAt(utcGuess);

  return new Date(utcGuess - offsetAt(firstPass));
};

// gloss
export const utcToZonedHour = (date: Date, timezone: string): number => {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour12: false,
    hour: '2-digit',
  });
  const hourStr = fmt.format(date);
  const hourNum = hourStr === '24' ? 0 : parseInt(hourStr, 10);
  return Number.isFinite(hourNum) ? hourNum : 0;
};

// gloss
export const utcToZonedCalendarDate = (utcInstant: Date, timezone: string): Date => {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = fmt.formatToParts(utcInstant);
  const get = (t: Intl.DateTimeFormatPartTypes) => parseInt(parts.find((p) => p.type === t)?.value ?? '0', 10);
  return new Date(get('year'), get('month') - 1, get('day'));
};

// gloss
export const formatHourSlot = (hour: number): string => `${String(hour).padStart(2, '0')}:00`;

// gloss
export const getBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIMEZONE;
  } catch {
    return DEFAULT_TIMEZONE;
  }
};

const pad = (value: number): string => String(value).padStart(2, '0');

// gloss
export const utcToZonedWallClock = (utcIsoDate: string, timezone: string): ZonedWallClock => {
  const instant = new Date(utcIsoDate);
  if (Number.isNaN(instant.getTime())) return { date: null, time: null };

  return {
    date: utcToZonedCalendarDate(instant, timezone),
    time: convertTo24Hour(convertToLocalTimeZone(utcIsoDate, timezone).time),
  };
};

export const zonedWallClockToUtcIso = ({ date, time }: ZonedWallClock, timezone: string): string | null => {
  if (!date || !time) return null;

  const isoDate = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

  return zonedWallClockToUtc(isoDate, time, timezone).toISOString();
};
